import type { AddressServerState, AddressStatus } from '@bartizo/shared';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * How an address's verdict reads (P11.1), the same on its page and in any list:
 * a badge whose colour says what to do — red is still open to it, green is
 * stopped, amber is half done.
 */

const STATUS_LABEL: Record<AddressStatus, string> = {
  stopped: 'Stopped',
  partly: 'Partly stopped',
  pending: 'Ban on its way',
  open: 'Not stopped',
  protected: 'Protected',
};

const STATUS_TONE: Record<AddressStatus, string> = {
  stopped: 'badge-success',
  partly: 'badge-warning',
  pending: 'badge-accent',
  open: 'badge-danger',
  protected: 'badge-muted',
};

const SERVER_STATE_LABEL: Record<AddressServerState, string> = {
  banned: 'Banned',
  pending: 'Ban on its way',
  failed: 'Ban failed',
  not_banned: 'Not banned',
  unknown: 'Cannot say',
};

const SERVER_STATE_TONE: Record<AddressServerState, string> = {
  banned: 'badge-success',
  pending: 'badge-accent',
  failed: 'badge-danger',
  not_banned: 'badge-warning',
  unknown: 'badge-muted',
};

export function AddressStatusBadge({ status }: { status: AddressStatus }): ReactNode {
  return <span className={`badge ${STATUS_TONE[status]}`}>{STATUS_LABEL[status]}</span>;
}

export function ServerStateBadge({ state }: { state: AddressServerState }): ReactNode {
  return <span className={`badge ${SERVER_STATE_TONE[state]}`}>{SERVER_STATE_LABEL[state]}</span>;
}

/** An address that opens its page: wherever an address is shown, this is what it is. */
export function AddressLink({ ip }: { ip: string }): ReactNode {
  return (
    <Link to={`/addresses/${encodeURIComponent(ip)}`} className="mono">
      {ip}
    </Link>
  );
}
